'use client'

import { useState, useEffect } from "react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { GradientButton } from "@/components/ui/gradient-button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { ArrowLeft, Download, Heart, Share2, FileText, Loader2, Scale, Check } from "lucide-react"
import Link from "next/link"
import Image from "next/image"
import { toast } from "sonner"
import { Breadcrumb } from "@/components/breadcrumb"
import { useComparison } from "@/lib/comparison-context"
import { useWishlist } from "@/lib/wishlist-context"
import { useQuoteCart } from "@/lib/quote-cart-context"
import { useCurrency } from "@/lib/currency-context"
import { Product } from "@/lib/types"
import { cn } from "@/lib/utils"
import { PdfViewerDialog } from "@/components/pdf-viewer-dialog"

interface ProductData {
  id: string
  part_number: string
  title: string | null
  description: string | null
  package_type: string | null
  resistance_ohms: number | null
  capacitance_farads: number | null
  voltage_volts: number | null
  current_amps: number | null
  power_watts: number | null
  frequency_hz: number | null
  tolerance_percent: number | null
  temperature_coefficient: string | null
  operating_temp_min: number | null
  operating_temp_max: number | null
  specs: Record<string, any>
  datasheets: string[]
  images: string[]
  lead_time_weeks: number | null
  manufacturer: {
    id: number
    name: string
    website: string | null
    logo_url: string | null
  }
  category: {
    id: number
    top_category: string
    mid_category: string
    leaf_category: string
  }
}

interface ProductPageProps {
  params: { id: string }
}

export default function ProductDetailPage({ params }: { params: { id: string } }) {
  const router = useRouter()
  const [product, setProduct] = useState<ProductData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false)
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string>('')
  
  const { comparedProducts, addToComparison, removeFromComparison, isInComparison, setIsComparisonOpen } = useComparison()
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist()
  const { addToQuoteCart } = useQuoteCart()
  const { formatPrice, currency } = useCurrency()

  useEffect(() => {
    async function fetchProduct() {
      try {
        setLoading(true)
        const response = await fetch(`/api/products/${params.id}`)
        
        if (!response.ok) {
          throw new Error('Product not found')
        }
        
        const data = await response.json()
        setProduct(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load product')
        toast.error('Failed to load product details')
      } finally {
        setLoading(false)
      }
    }

    fetchProduct()
  }, [params.id])

  // Helper function to convert ProductData to Product
  const convertToProduct = (productData: ProductData): Product => {
    return {
      id: productData.id,
      partNumber: productData.part_number,
      manufacturer: productData.manufacturer.name,
      manufacturerId: String(productData.manufacturer.id),
      title: productData.title || productData.part_number,
      description: productData.description || '',
      category: productData.category.top_category,
      subcategory: productData.category.mid_category,
      series: undefined,
      price: 0, // RFQ - no fixed price
      currency: currency,
      stock: 'On Order', // Default for RFQ products
      moq: 1,
      leadTime: productData.lead_time_weeks ? `${productData.lead_time_weeks} weeks` : 'Contact Sales',
      packageType: productData.package_type || 'N/A',
      lifecycle: 'Active',
      rohs: true,
      specifications: {
        ...(productData.resistance_ohms && { 'Resistance': `${productData.resistance_ohms} Ω` }),
        ...(productData.capacitance_farads && { 'Capacitance': `${productData.capacitance_farads} F` }),
        ...(productData.voltage_volts && { 'Voltage': `${productData.voltage_volts} V` }),
        ...(productData.current_amps && { 'Current': `${productData.current_amps} A` }),
        ...(productData.power_watts && { 'Power': `${productData.power_watts} W` }),
        ...(productData.frequency_hz && { 'Frequency': `${productData.frequency_hz} Hz` }),
        ...(productData.tolerance_percent && { 'Tolerance': `±${productData.tolerance_percent}%` }),
        ...(productData.temperature_coefficient && { 'Temp Coefficient': productData.temperature_coefficient }),
        ...(productData.operating_temp_min && { 'Min Temp': `${productData.operating_temp_min}°C` }),
        ...(productData.operating_temp_max && { 'Max Temp': `${productData.operating_temp_max}°C` }),
        ...(productData.specs || {})
      },
      documents: productData.datasheets?.map((url, i) => ({
        id: `${productData.id}-doc-${i}`,
        name: `Datasheet ${i + 1}`,
        type: 'Datasheet' as const,
        format: 'PDF' as const,
        size: 'N/A',
        url: url,
        downloadCount: 0
      })) || [],
      images: productData.images || ['/placeholder.svg'],
      relatedProducts: [],
      applications: [],
      tags: [productData.category.top_category, productData.category.mid_category, productData.package_type].filter(Boolean) as string[],
      createdAt: new Date(),
      updatedAt: new Date()
    }
  }

  const handleAddToFavorites = () => {
    if (!product) return
    
    // Convert ProductData to Product type for wishlist
    const productForWishlist: Product = convertToProduct(product)
    
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id)
      toast.success(`Removed ${product.part_number} from wishlist!`)
    } else {
      addToWishlist(productForWishlist)
      toast.success(`Added ${product.part_number} to wishlist!`)
    }
  }

  const handleShareProduct = () => {
    if (!product) return
    
    if (navigator.share) {
      navigator.share({
        title: product.part_number,
        text: product.description || product.title || '',
        url: window.location.href,
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      toast.success("Product link copied to clipboard!")
    }
  }

  const handleRequestQuote = () => {
    if (product) {
      const productForCart = convertToProduct(product)
      addToQuoteCart(productForCart, 1)
      toast.success(`${product.part_number} added to quote cart!`, {
        action: {
          label: 'View Quote',
          onClick: () => router.push('/quote')
        }
      })
    }
  }

  const handleCompareProduct = () => {
    if (!product) return
    
    const productForComparison = convertToProduct(product)
    
    const isCurrentlyCompared = isInComparison(product.id)
    
    if (isCurrentlyCompared) {
      removeFromComparison(product.id)
      toast.success(`Removed ${product.part_number} from comparison`)
    } else {
      const canAdd = comparedProducts.length < 4
      if (canAdd) {
        addToComparison(productForComparison)
        toast.success(`Added ${product.part_number} to comparison`)
        
        // Optionally open the drawer
        if (comparedProducts.length >= 1) {
          setTimeout(() => setIsComparisonOpen(true), 500)
        }
      } else {
        toast.error('Maximum 4 products can be compared. Remove one to add this product.')
      }
    }
  }

  const handleDownloadDatasheet = (url: string, index: number) => {
    setCurrentPdfUrl(url)
    setPdfViewerOpen(true)
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-accent-blue mx-auto mb-4" />
          <p className="text-slate-600">Loading product details...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !product) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Breadcrumb items={[
          { href: "/", label: "Home" },
          { href: "/products", label: "Products" },
          { label: "Error", current: true }
        ]} />
        
        <main className="container mx-auto px-6 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Product Not Found</h2>
              <p className="text-slate-600 mb-6">
                {error || "The product you're looking for doesn't exist or has been removed."}
              </p>
              <Button asChild>
                <Link href="/products">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Products
                </Link>
              </Button>
            </CardContent>
          </Card>
        </main>
      </div>
    )
  }

  // Build specifications object
  const specifications: Record<string, string> = {
    ...(product.resistance_ohms && { 'Resistance': `${product.resistance_ohms} Ω` }),
    ...(product.capacitance_farads && { 'Capacitance': `${product.capacitance_farads} F` }),
    ...(product.voltage_volts && { 'Voltage': `${product.voltage_volts} V` }),
    ...(product.current_amps && { 'Current': `${product.current_amps} A` }),
    ...(product.power_watts && { 'Power': `${product.power_watts} W` }),
    ...(product.frequency_hz && { 'Frequency': `${product.frequency_hz} Hz` }),
    ...(product.tolerance_percent && { 'Tolerance': `±${product.tolerance_percent}%` }),
    ...(product.temperature_coefficient && { 'Temperature Coefficient': product.temperature_coefficient }),
    ...(product.operating_temp_min && { 'Operating Temp Min': `${product.operating_temp_min}°C` }),
    ...(product.operating_temp_max && { 'Operating Temp Max': `${product.operating_temp_max}°C` }),
    ...(product.package_type && { 'Package': product.package_type }),
    ...(product.specs || {})
  }

  const hasSpecifications = Object.keys(specifications).length > 0

  return (
    <div className="min-h-screen bg-slate-50">
      <Breadcrumb items={[
        { href: "/", label: "Home" },
        { href: "/products", label: "Products" },
        { href: `/products?category=${product.category.top_category.toLowerCase()}`, label: product.category.top_category },
        { label: product.part_number, current: true }
      ]} />

      <main className="container mx-auto px-6 py-8">
        {/* Back Button */}
        <div className="mb-6">
          <Link href="/products" className="inline-flex items-center text-slate-600 hover:text-accent-blue transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Product Catalog
          </Link>
        </div>

        {/* Mobile-only compact layout: smaller image, primary actions and collapsed specs */}
        <div className="lg:hidden space-y-4">
          {/* Compact Image */}
          <Card>
            <CardContent className="p-0">
              <div className="relative w-full h-44 bg-gradient-to-br from-slate-50 to-white">
                <Image
                  src={product.images && product.images[0] ? product.images[0] : '/placeholder.svg'}
                  alt={product.part_number}
                  fill
                  className="object-contain p-6"
                  unoptimized
                />
              </div>
            </CardContent>
          </Card>

          {/* Title, badges and short description */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline">{product.category.top_category}</Badge>
              <Badge variant="secondary">{product.category.mid_category}</Badge>
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-1">{product.part_number}</h1>
            <p className="text-sm text-slate-600 mb-2">{product.manufacturer.name}</p>

            {product.description && (
              <p className="text-sm text-slate-700 line-clamp-3">{product.description}</p>
            )}
          </div>

          {/* Primary CTAs */}
          <div className="space-y-3">
            <GradientButton variant="blue-red" size="lg" className="w-full" onClick={handleRequestQuote}>
              <FileText className="h-5 w-5 mr-2" />
              Request Quote
            </GradientButton>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className={cn(
                  "flex-1 transition-all duration-200",
                  product && isInWishlist(product.id)
                    ? "bg-red-50 border-red-200 text-red-600 hover:bg-red-100"
                    : "hover:bg-red-50 hover:border-red-200 hover:text-red-600"
                )}
                onClick={handleAddToFavorites}
              >
                <Heart className={cn("h-4 w-4 mr-1", product && isInWishlist(product.id) && "fill-current")} />
                {product && isInWishlist(product.id) ? 'Saved' : 'Save'}
              </Button>
              <Button variant="outline" className="flex-1" onClick={handleShareProduct}>
                <Share2 className="h-4 w-4 mr-1" />
                Share
              </Button>
            </div>

            {/* Collapsible key specs for mobile */}
            {hasSpecifications && (
              <details className="bg-white rounded-lg border border-slate-100 p-3">
                <summary className="font-medium cursor-pointer">Key Specifications</summary>
                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
                  {Object.entries(specifications).slice(0, 6).map(([key, value]) => (
                    <div key={key} className="flex flex-col">
                      <span className="text-xs text-slate-500">{key}</span>
                      <span className="font-medium text-slate-900">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-8">
          {/* Left - Product Image (hidden on mobile) */}
          <div className="hidden lg:block lg:col-span-4 order-1">
            {product.images && product.images.length > 0 && (
              <Card className="sticky top-4 overflow-hidden">
                <CardContent className="p-0">
                  <div className="relative w-full aspect-square bg-gradient-to-br from-slate-50 to-white">
                    <Image
                      src={product.images[0]}
                      alt={product.part_number}
                      fill
                      className="object-contain p-8"
                      unoptimized
                    />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Middle - Product Details */}
          <div className="lg:col-span-5 order-2">
            {/* Product Header (hidden on mobile to avoid duplication with compact mobile layout) */}
            <div className="mb-6 hidden lg:block">
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline">{product.category.top_category}</Badge>
                <Badge variant="secondary">{product.category.mid_category}</Badge>
              </div>
              
              <h1 className="text-3xl font-bold text-slate-900 mb-2">
                {product.part_number}
              </h1>
              
              <p className="text-lg text-slate-600 mb-3">{product.manufacturer.name}</p>
              
              {product.description && (
                <p className="text-slate-700 leading-relaxed">{product.description}</p>
              )}
              {!product.description && product.title && product.title !== product.part_number && (
                <p className="text-slate-700 leading-relaxed">{product.title}</p>
              )}
            </div>

            {/* Key Specifications (hidden on mobile — mobile uses a compact collapsible) */}
            {hasSpecifications && (
              <Card className="mb-6 hidden lg:block">
                <CardHeader>
                  <CardTitle>Key Specifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {Object.entries(specifications).slice(0, 8).map(([key, value]) => (
                      <div key={key} className="flex flex-col">
                        <span className="text-xs text-slate-500 mb-1">{key}</span>
                        <span className="font-medium text-slate-900">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Full Specifications (hidden on mobile to reduce duplicate content) */}
            {hasSpecifications && Object.keys(specifications).length > 8 && (
              <Card className="mb-6 hidden lg:block">
                <CardHeader>
                  <CardTitle>Complete Specifications</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-2">
                    {Object.entries(specifications).map(([key, value]) => (
                      <div key={key} className="flex justify-between py-2 border-b border-slate-100 last:border-b-0">
                        <span className="text-sm text-slate-600">{key}:</span>
                        <span className="text-sm font-medium text-slate-900">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Documents */}
            {product.datasheets && product.datasheets.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Technical Documents</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {product.datasheets.map((url, index) => (
                      <div key={index} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <FileText className="h-5 w-5 text-slate-400" />
                          <div>
                            <h4 className="font-medium text-slate-900">Datasheet {index + 1}</h4>
                            <p className="text-sm text-slate-600">PDF Document</p>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => handleDownloadDatasheet(url, index)}>
                          <FileText className="h-4 w-4 mr-2" />
                          View
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Manufacturer Info */}
            {product.manufacturer && (
              <Card>
                <CardHeader>
                  <CardTitle>Manufacturer</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    {product.manufacturer.logo_url && (
                      <div className="relative w-16 h-16 bg-white border border-slate-200 rounded-lg overflow-hidden flex-shrink-0">
                        <Image
                          src={product.manufacturer.logo_url}
                          alt={product.manufacturer.name}
                          fill
                          className="object-contain p-2"
                          unoptimized
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900 mb-1">{product.manufacturer.name}</p>
                      {product.manufacturer.website && (
                        <a 
                          href={product.manufacturer.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-accent-blue hover:underline inline-flex items-center gap-1"
                        >
                          Visit Website
                          <Share2 className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right - Action Sidebar (Sticky, hidden on mobile) */}
          <div className="hidden lg:block lg:col-span-3 order-3">
            <div className="sticky top-4 space-y-4">
              {/* Primary Actions Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Product Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Availability Badge */}
                  <div className="text-center py-2">
                    <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                      RFQ Available
                    </Badge>
                  </div>

                  {/* Product Info */}
                  <div className="space-y-3 py-3 border-y">
                    {product.package_type && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Package:</span>
                        <span className="font-medium text-slate-900">{product.package_type}</span>
                      </div>
                    )}
                    {product.lead_time_weeks && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">Lead Time:</span>
                        <span className="font-medium text-slate-900">{product.lead_time_weeks} weeks</span>
                      </div>
                    )}
                  </div>

                  {/* Main CTA */}
                  <GradientButton 
                    variant="blue-red" 
                    size="lg"
                    className="w-full" 
                    onClick={handleRequestQuote}
                  >
                    <FileText className="h-5 w-5 mr-2" />
                    Request Quote
                  </GradientButton>

                  {/* Secondary Actions */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Button 
                      variant="outline"
                      className={cn(
                        "transition-all duration-200",
                        product && isInWishlist(product.id)
                          ? "bg-red-50 border-red-200 text-red-600 hover:bg-red-100"
                          : "hover:bg-red-50 hover:border-red-200 hover:text-red-600"
                      )}
                      onClick={handleAddToFavorites}
                    >
                      <Heart className={cn("h-4 w-4 mr-1", product && isInWishlist(product.id) && "fill-current")} />
                      {product && isInWishlist(product.id) ? 'Saved' : 'Save'}
                    </Button>
                    <Button variant="outline" onClick={handleShareProduct}>
                      <Share2 className="h-4 w-4 mr-1" />
                      Share
                    </Button>
                  </div>

                  {/* Compare Button */}
                  {product && isInComparison(product.id) ? (
                    <GradientButton 
                      variant="blue-red"
                      className="w-full"
                      onClick={handleCompareProduct}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      In Comparison ({comparedProducts.length}/4)
                    </GradientButton>
                  ) : (
                    <Button 
                      variant="outline"
                      className="w-full"
                      onClick={handleCompareProduct}
                    >
                      <Scale className="h-4 w-4 mr-2" />
                      Compare Product
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Quick Info Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Quick Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Part Number:</span>
                    <span className="font-medium text-slate-900">{product.part_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Category:</span>
                    <span className="font-medium text-slate-900 text-xs">{product.category.leaf_category}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      {/* PDF Viewer Dialog */}
      <PdfViewerDialog
        open={pdfViewerOpen}
        onOpenChange={setPdfViewerOpen}
        pdfUrl={currentPdfUrl}
        title={product?.part_number ? `${product.part_number} - Datasheet` : 'Datasheet'}
      />
    </div>
  )
}
